import type { EmbeddingFailureReason, EmbeddingsCatalogCoverage } from '@data-hub/shared/metrics';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertTriangleIcon, CheckCircle2Icon, RefreshCwIcon, XCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
import { AdminGuard } from '@/components/layout/AdminGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useEmbeddingsHealth,
  useReconcileEmbeddings,
} from '@/features/metric-hub/lib/use-embeddings-health';
import { handleApiError } from '@/lib/handle-error';

export const Route = createFileRoute('/_authed/admin/embeddings')({
  component: AdminEmbeddingsPage,
});

function AdminEmbeddingsPage() {
  return (
    <AdminGuard>
      <EmbeddingsAdmin />
    </AdminGuard>
  );
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function EmbeddingsAdmin() {
  const health = useEmbeddingsHealth();
  const reconcile = useReconcileEmbeddings();

  const runReconcile = () => {
    reconcile.mutate(undefined, {
      onSuccess: (h) => {
        const healed = h.healed.metrics + h.healed.semanticModels;
        toast.success(
          healed > 0
            ? `Reconcile done — embedded or refreshed ${healed} item(s).`
            : 'Reconcile done — all up to date.',
        );
      },
      onError: (err) => handleApiError(err, 'Reconcile failed'),
    });
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Embeddings health</h1>
          <p className="text-sm text-muted-foreground">
            Semantic-similarity coverage for metrics and semantic models — powers duplicate
            detection and the Brain Graph.
          </p>
        </div>
        <Button onClick={runReconcile} disabled={reconcile.isPending}>
          <RefreshCwIcon className={reconcile.isPending ? 'size-4 animate-spin' : 'size-4'} />
          {reconcile.isPending ? 'Reconciling…' : 'Reconcile now'}
        </Button>
      </div>

      {health.isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-24 w-full" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      ) : health.isError ? (
        <Card className="flex flex-col items-start gap-3 border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Couldn't load embeddings health.
          <Button
            variant="outline"
            size="sm"
            onClick={() => void health.refetch()}
            disabled={health.isFetching}
          >
            <RefreshCwIcon className={health.isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Try again
          </Button>
        </Card>
      ) : !health.data ? (
        <Card className="flex flex-col items-start gap-2 p-6">
          <p className="text-sm font-medium">No reconcile has run yet.</p>
          <p className="text-sm text-muted-foreground">
            Run a reconcile to probe the embedding service and measure coverage.
          </p>
        </Card>
      ) : (
        <>
          <ServiceCard
            service={health.data.service}
            ranAt={health.data.ranAt}
            ranBy={health.data.ranBy}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <CoverageCard
              title="Metrics"
              coverage={health.data.metrics}
              healed={health.data.healed.metrics}
              linkFor={(id) => ({ to: '/metric-hub/metrics/$slug', params: { slug: id } })}
            />
            <CoverageCard
              title="Semantic models"
              coverage={health.data.semanticModels}
              healed={health.data.healed.semanticModels}
              linkFor={(id) => ({ to: '/metric-hub/semantic-models/$name', params: { name: id } })}
            />
          </div>
        </>
      )}
    </div>
  );
}

function ServiceCard({
  service,
  ranAt,
  ranBy,
}: {
  service: {
    healthy: boolean;
    modelLoaded: boolean;
    checkedAt: string;
    reason?: string;
    modelName?: string;
    modelVersion?: string;
  };
  ranAt: string;
  ranBy: string;
}) {
  return (
    <Card
      className={
        service.healthy
          ? 'flex flex-wrap items-center gap-x-6 gap-y-2 border-emerald-500/30 bg-emerald-500/5 p-4'
          : 'flex flex-wrap items-center gap-x-6 gap-y-2 border-destructive/40 bg-destructive/5 p-4'
      }
    >
      <div className="flex items-center gap-2">
        {service.healthy ? (
          <CheckCircle2Icon className="size-5 text-emerald-600 dark:text-emerald-400" />
        ) : (
          <XCircleIcon className="size-5 text-destructive" />
        )}
        <span className="text-sm font-semibold">
          Service {service.healthy ? 'healthy' : 'unhealthy'}
        </span>
        {!service.healthy && service.reason && (
          <Badge variant="outline" className="border-destructive/40 text-destructive">
            {service.reason}
          </Badge>
        )}
      </div>
      {service.modelName && (
        <span className="text-xs text-muted-foreground">
          {service.modelName}
          {service.modelVersion ? ` · v${service.modelVersion}` : ''}
        </span>
      )}
      <span className="text-xs text-muted-foreground">
        Last reconcile {formatDateTime(ranAt)} · {ranBy}
      </span>
    </Card>
  );
}

const FAILURE_LABEL: Record<EmbeddingFailureReason, string> = {
  no_answer: 'didn’t answer in time',
  rejected: 'text refused',
  service_down: 'service went down',
};

function CoverageCard({
  title,
  coverage,
  healed,
  linkFor,
}: {
  title: string;
  coverage: EmbeddingsCatalogCoverage;
  healed: number;
  linkFor: (id: string) => { to: string; params: Record<string, string> };
}) {
  const pct = Math.round(coverage.coverage * 100);
  const low = pct < 80;
  const why = new Map(coverage.failures.map((f) => [f.id, f.reason]));
  const staleFailed = coverage.failures.filter((f) => !coverage.missingIds.includes(f.id));
  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">{title}</h2>
        <span
          className={
            low
              ? 'text-2xl font-bold tabular-nums text-amber-600 dark:text-amber-400'
              : 'text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400'
          }
        >
          {pct}%
        </span>
      </div>
      <Progress value={pct} className="h-1.5" />
      <p className="text-xs text-muted-foreground">
        {coverage.embedded} / {coverage.total} embedded
        {coverage.missing > 0 ? ` · ${coverage.missing} missing` : ''}
        {coverage.stale > 0 ? ` · ${coverage.stale} out of date` : ''}
        {healed > 0 ? ` · healed ${healed} this run` : ''}
      </p>
      {coverage.missingIds.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <AlertTriangleIcon className="size-3 text-amber-500" />
            Not embedded
          </p>
          <div className="flex flex-wrap gap-1">
            {coverage.missingIds.slice(0, 30).map((id) => {
              const l = linkFor(id);
              const reason = why.get(id);
              return (
                <Link
                  key={id}
                  to={l.to}
                  params={l.params as never}
                  className="rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] hover:bg-muted"
                  title={
                    reason ? `Last pass: ${FAILURE_LABEL[reason]}` : 'Not tried in the last pass'
                  }
                >
                  {id}
                  {reason && (
                    <span className="ml-1 font-sans text-amber-700 dark:text-amber-300">
                      · {FAILURE_LABEL[reason]}
                    </span>
                  )}
                </Link>
              );
            })}
            {coverage.missingIds.length > 30 && (
              <span className="px-1 py-0.5 text-[10px] text-muted-foreground">
                +{coverage.missingIds.length - 30} more
              </span>
            )}
          </div>
        </div>
      )}
      {staleFailed.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Out of date and not re-embedded:{' '}
          {staleFailed.map((f, i) => (
            <span key={f.id}>
              {i > 0 && ', '}
              <span className="font-mono">{f.id}</span> ({FAILURE_LABEL[f.reason]})
            </span>
          ))}
        </p>
      )}
    </Card>
  );
}
